/**
 * 최상위 랜딩 (`/`) — VSMR 이중 정체성의 2택 관문 (2026-08-11 결정).
 *
 * 화면은 두 히어로 패널이 전부다(순수 2택): ① 저작·실행 플랫폼(→ /platform
 * 최상위 Overview) vs ② 운전 시뮬레이터(→ /operation-hmi/, 별도 앱). 이관 대시보드
 * (이슈 게시판·Recent Activity·Tutorial)는 랜딩이 아니라 /platform 에 산다.
 *
 * 사이드바 없는 풀블리드 다크 랜딩 — 앱 테마와 무관하게 자체 팔레트를 쓴다
 * (선택 후 작업 공간부터 AppLayout·테마가 적용되는 서사).
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Chip, MenuItem, Paper, Stack, Typography } from '@mui/material';
import { Menu } from '@/shared/ui/overlays';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAuthStore } from '@/app/stores/authStore';
import { useLogout } from '@/features/auth/useLogout';
import { formatMonthDayWeekday } from '@/shared/dateTime';

/** 랜딩 자체 팔레트 — 앱 테마(라이트/다크) 어느 쪽에서 진입해도 동일한 얼굴 */
const L = {
  bg: 'radial-gradient(1200px 600px at 50% -10%, #12305a 0%, #0a1428 45%, #060b18 100%)',
  panel: 'rgba(13, 26, 48, 0.75)',
  panelBorder: 'rgba(90, 140, 210, 0.25)',
  panelHover: 'rgba(120, 180, 255, 0.55)',
  text: '#e6edf7',
  sub: '#8fa8c8',
  accent: '#4db8ff',
} as const;

type HeroDestination =
  | { readonly kind: 'internal'; readonly to: string }
  | { readonly kind: 'external'; readonly href: string };

interface HeroPanelProps {
  readonly title: string;
  readonly subtitle: string;
  readonly bullets: readonly string[];
  readonly art: React.ReactNode;
  readonly badge?: string;
  readonly destination: HeroDestination;
}

const HeroPanel: React.FC<HeroPanelProps> = ({ title, subtitle, bullets, art, badge, destination }) => {
  const content = (
    <>
      <Box sx={{ height: 150, display: 'flex', alignItems: 'center' }}>{art}</Box>
      <Stack spacing={0.5} alignItems="center">
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.5, color: L.text }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" sx={{ color: L.accent, fontWeight: 500 }}>
            {subtitle}
          </Typography>
          {badge && (
            <Chip
              label={badge}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                color: '#ffd479',
                bgcolor: 'rgba(255, 190, 80, 0.12)',
                border: '1px solid rgba(255, 190, 80, 0.4)',
              }}
            />
          )}
        </Stack>
      </Stack>
      <Stack spacing={0.75} sx={{ mt: 1, alignSelf: 'center', alignItems: 'flex-start', textAlign: 'left' }}>
        {bullets.map((bullet) => (
          <Typography key={bullet} variant="body2" sx={{ color: L.sub }}>
            • {bullet}
          </Typography>
        ))}
      </Stack>
      <Box
        aria-hidden
        sx={{
          mt: 'auto',
          px: 2,
          py: 0.75,
          borderRadius: 1,
          border: `1px solid ${L.panelBorder}`,
          color: L.accent,
          fontSize: '0.875rem',
          '.MuiPaper-root:hover &, .MuiPaper-root:focus-visible &': {
            borderColor: L.accent,
            backgroundColor: 'rgba(77, 184, 255, 0.08)',
          },
        }}
      >
        들어가기
      </Box>
    </>
  );
  const sx = {
      flex: 1,
      minWidth: 0,
      p: { xs: 3, md: 5 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 2,
      cursor: 'pointer',
      background: L.panel,
      border: `1px solid ${L.panelBorder}`,
      borderRadius: 3,
      color: L.text,
      textDecoration: 'none',
      transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover, &:focus-visible': {
        borderColor: L.panelHover,
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 40px rgba(30, 90, 160, 0.35)`,
      },
    } as const;

  return destination.kind === 'external' ? (
    <Paper component="a" href={destination.href} elevation={0} aria-label={badge ? `${title} — ${subtitle} (${badge})` : `${title} — ${subtitle}`} sx={sx}>
      {content}
    </Paper>
  ) : (
    <Paper component={Link} to={destination.to} elevation={0} aria-label={badge ? `${title} — ${subtitle} (${badge})` : `${title} — ${subtitle}`} sx={sx}>
      {content}
    </Paper>
  );
};

/** ① 패널 아트 — 메시·차트 모티프 (저작·해석) */
const PlatformArt: React.FC = () => (
  <svg width="220" height="140" viewBox="0 0 220 140" fill="none" aria-hidden>
    <rect x="14" y="12" width="128" height="92" rx="8" stroke={L.accent} strokeOpacity="0.7" />
    <path d="M28 88 L58 56 L84 70 L118 34" stroke="#7ee0a3" strokeWidth="2" fill="none" />
    <path d="M28 92 L64 74 L96 84 L118 62" stroke={L.accent} strokeWidth="2" fill="none" strokeOpacity="0.8" />
    {[28, 58, 84, 118].map((x, i) => (
      <circle key={x} cx={x} cy={[88, 56, 70, 34][i]} r="3" fill="#7ee0a3" />
    ))}
    <g stroke={L.accent} strokeOpacity="0.5">
      <path d="M156 30 h48 M156 50 h48 M156 70 h48 M156 90 h48" />
      <path d="M160 22 v76 M184 22 v76 M204 22 v76" />
    </g>
    <circle cx="184" cy="50" r="6" stroke="#ffd479" fill="none" />
  </svg>
);

/** ② 패널 아트 — 일체형 원자로·게이지 모티프 (운전) */
const OperationArt: React.FC = () => (
  <svg width="220" height="140" viewBox="0 0 220 140" fill="none" aria-hidden>
    {/* 일체형 압력용기 */}
    <path
      d="M60 24 h44 a10 10 0 0 1 10 10 v66 a10 10 0 0 1 -10 10 h-44 a10 10 0 0 1 -10 -10 v-66 a10 10 0 0 1 10 -10 z"
      stroke={L.accent}
      strokeOpacity="0.8"
    />
    <rect x="70" y="66" width="24" height="30" rx="2" stroke="#ff9e6d" />
    <path d="M66 34 q16 10 32 0 M66 44 q16 10 32 0 M66 54 q16 10 32 0" stroke="#7ee0a3" strokeOpacity="0.9" fill="none" />
    <rect x="74" y="14" width="16" height="10" rx="3" stroke={L.accent} strokeOpacity="0.6" />
    {/* 게이지 */}
    <path d="M144 96 a34 34 0 1 1 68 0" stroke={L.accent} strokeOpacity="0.6" fill="none" />
    <path d="M144 96 a34 34 0 0 1 20 -31" stroke="#7ee0a3" strokeWidth="3" fill="none" />
    <line x1="178" y1="96" x2="164" y2="72" stroke="#ffd479" strokeWidth="2" />
    <circle cx="178" cy="96" r="3" fill="#ffd479" />
  </svg>
);

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const userEmail = useAuthStore((s) => s.user?.email);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const today = formatMonthDayWeekday();

  return (
    <Box sx={{ minHeight: '100vh', background: L.bg, color: L.text }}>
      {/* 상단 바 — 워드마크 + 날짜 + 계정 */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ px: { xs: 2, md: 4 }, py: 2, gap: 2 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 2 }}>
          VSMR
        </Typography>
        <Typography variant="body2" sx={{ color: L.sub, display: { xs: 'none', sm: 'block' } }}>
          Virtual SMR Simulation Platform
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ color: L.sub }}>
          {today}
        </Typography>
        <Button
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          startIcon={<AccountCircleOutlinedIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          aria-haspopup="menu"
          aria-expanded={menuAnchor !== null}
          aria-controls={menuAnchor !== null ? 'landing-account-menu' : undefined}
          sx={{ color: L.text, textTransform: 'none' }}
        >
          {userEmail ?? '계정'}
        </Button>
        <Menu
          id="landing-account-menu"
          anchorEl={menuAnchor}
          open={menuAnchor !== null}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              navigate('/settings');
            }}
          >
            계정 설정
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              handleLogout();
            }}
          >
            로그아웃
          </MenuItem>
        </Menu>
      </Stack>

      {/* 히어로 — 2택 */}
      <Box
        sx={{
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, md: 6 },
          py: { xs: 4, md: 0 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 5 }}
          sx={{ width: '100%', maxWidth: 1200, mx: 'auto', alignItems: 'stretch' }}
        >
          <HeroPanel
            title="Modeling & Simulation"
            subtitle="저작·실행 플랫폼"
            bullets={[
              'MARS·FMU·Modelica 모델 저작과 검증',
              '멀티 코드 커플링 기반 실시간 시뮬레이션',
              '라이브 스트림 관측과 결과 해석',
            ]}
            art={<PlatformArt />}
            destination={{ kind: 'internal', to: '/platform' }}
          />
          <HeroPanel
            title="Operation Simulator"
            subtitle="운전 시뮬레이터"
            badge="프로토타입"
            bullets={[
              'SMART 참조 플랜트 실시간 운전 — 1·2차측 커플링',
              '시나리오 운전·고장 주입 훈련, 다중 인스턴스',
              '사용자 저작 시뮬레이터·자율운전 API (예정)',
            ]}
            art={<OperationArt />}
            destination={{ kind: 'external', href: '/operation-hmi/' }}
          />
        </Stack>
      </Box>

    </Box>
  );
};

export default LandingPage;
